import { createAsyncThunk } from "@reduxjs/toolkit";
import { ProcurementAxios } from "api/ProcurementAxios";
import { WarehouseAxios } from "api/WarehouseAxios";



export const fetchDraftRFQs = createAsyncThunk(
  "procurement/fetchDraftRFQs",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (
            filterField &&
            filterValue !== undefined &&
            filterValue !== null
          ) {
            if (typeof filterValue === "object" && filterValue !== null) {
              // Handle cases like { from_date: ..., to_date: ... }
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
                if (
                  (filterValue.from_date || filterValue.to_date) &&
                  (filterField === "created_at" || filterField === "updated_at")
                ) {
                  if (filterField === "created_at") {
                    filterField = "created";
                  } else {
                    filterField = "updated";
                  }
                  params.append("filter_by", filterField);
                }
              });
            } else {
              // Handle primitive values (string, number)
              params.append(filterField, filterValue);
            }
          }
        }
      );
    }
    const response: any = await ProcurementAxios.get(
      `/rfq/list1/?${params.toString()}`
    );

    return {
      data: response?.data?.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const exportPendingPOCSV = async (warehouse_id: string) => {
  const response = await WarehouseAxios.get(
    `/item/pending_pos/?warehouse_id=${warehouse_id}&export=excel`,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "purchaseRequest.csv",
  };
};



export const exportDraftRFQCSV = async ({
  page,
  pageSize,
  sort,
  order,
  search,
  filters,
  warehouse_id,
}: {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: string;
  filters?: any[];
  warehouse_id?: any;
} = {}) => {
  // Build query parameters similar to fetchGRNList
  const queryParams = new URLSearchParams();
  
  // Add export flag
  queryParams.append('export', 'excel');

  // Add pagination (optional for export, but include if provided)
  if (page !== undefined) {
    queryParams.append('page', page.toString());
  }
  if (pageSize !== undefined) {
    queryParams.append('page_size', pageSize.toString());
  }

  // Add sorting
  if (sort) {
    queryParams.append('sort', sort);
    if (order) {
      queryParams.append('order', order);
    }
  }

  // Add search
  if (search) {
    queryParams.append('search', search);
  }

  // Add warehouse ID
  if (warehouse_id) {
    queryParams.append('warehouse_id', warehouse_id);
  }

  // Add filters
  if (filters && filters.length > 0) {
    filters.forEach((filter: any) => {
      if (filter.filterValue && filter.filterValue !== '') {
        // Handle object filters (like date ranges)
        if (typeof filter.filterValue === "object" && filter.filterValue !== null) {
          Object.entries(filter.filterValue).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              queryParams.append(key, String(value));
            }
          });
        } else {
          // Handle primitive values (string, number)
          queryParams.append(filter.filterField, filter.filterValue);
        }
      }
    });
  }

  const response = await WarehouseAxios.get(
    `/item/grn_list/?${queryParams.toString()}`,
    {
      responseType: "blob",
      headers: {
        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv, */*",
      },
    }
  );

  // Extract filename from content-disposition header
  let filename = "grn_list.xlsx";
  const disposition = response.headers["content-disposition"];
  if (disposition) {
    const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, "");
    }
  }

  // Check content-type to determine file extension
  const contentType = response.headers["content-type"] || "";
  if (contentType.includes("excel") || contentType.includes("spreadsheet")) {
    if (!filename.endsWith(".xlsx") && !filename.endsWith(".xls")) {
      filename = filename.replace(/\.[^.]+$/, "") + ".xlsx";
    }
  } else if (contentType.includes("csv") || filename.endsWith(".csv")) {
    if (!filename.endsWith(".csv")) {
      filename = filename.replace(/\.[^.]+$/, "") + ".csv";
    }
  }

  return {
    blob: response.data,
    filename: filename,
  };
};



